import { HttpEvent, HttpInterceptorFn, HttpResponse } from '@angular/common/http';
import { Observable, finalize, share } from 'rxjs';

/**
 * Läuft dieselbe GET-Anfrage bereits, wird sie geteilt statt ein zweites Mal gestellt.
 *
 * Beim Öffnen eines Dokuments fragen der Route-Guard und die Detailkomponente unabhängig
 * voneinander dieselbe Ressource ab. Gemessen am Konzept CON-0003 waren das zwei Aufrufe zu je
 * rund 5,5 Sekunden — der Nutzer wartete elf Sekunden auf einen Datensatz, den das Backend
 * bereits beim ersten Mal geliefert hatte.
 *
 * Bewusst **kein** Cache: Es wird ausschließlich eine bereits laufende Anfrage geteilt, und die
 * Zuordnung wird beim Abschluss wieder verworfen. Damit sieht niemand veraltete Daten, und die
 * Berechtigungsprüfung des Backends läuft weiterhin bei jeder neuen Anfrage.
 */
const inFlight = new Map<string, Observable<HttpEvent<unknown>>>();

export const inFlightDedupInterceptor: HttpInterceptorFn = (request, next) => {
  // Nur GET: mutierende Anfragen dürfen niemals zusammengefasst werden.
  if (request.method !== 'GET') {
    return next(request);
  }

  const key = request.urlWithParams;
  const running = inFlight.get(key);
  if (running) {
    return running;
  }

  const shared = next(request).pipe(
    // `share` lässt beide Abonnenten dieselbe Antwort sehen; ohne das würde der zweite
    // Abonnent die Anfrage erneut auslösen.
    share(),
    finalize(() => inFlight.delete(key))
  );
  inFlight.set(key, shared);
  return shared;
};

/** Nur für Tests: verwirft die laufenden Zuordnungen. */
export function resetInFlightRequests(): void {
  inFlight.clear();
}

/** Nur für Tests: prüft, ob eine Antwort aus einer geteilten Anfrage stammt. */
export function isHttpResponse(event: HttpEvent<unknown>): event is HttpResponse<unknown> {
  return event instanceof HttpResponse;
}
